import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PostcodeLookup } from "@/components/PostcodeLookup";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Home, Building2, PoundSterling, Key, FileText, Send, Upload,
  CheckCircle2, Circle, Loader2, Plus, Calendar, ExternalLink
} from "lucide-react";
import { API } from "@/App";

export const AddInstructionModal = ({ open, onOpenChange, applicantId, applicantName, onSuccess }) => {
  const token = localStorage.getItem("token");
  const [loading, setLoading] = useState(false);
  const [instructionType, setInstructionType] = useState("sales");
  
  const [formData, setFormData] = useState({
    // Address
    address_line1: "",
    address_line2: "",
    town: "",
    county: "",
    postcode: "",
    
    // Property details
    property_type: "semi_detached",
    bedrooms: 3,
    bathrooms: 1,
    receptions: 1,
    
    // Sales specific
    asking_price: "",
    agency_type: "sole",
    fee_percentage: 1.25,
    fee_fixed: "",
    instruction_date: new Date().toISOString().split("T")[0],
    marketing_start_date: "",
    
    // Lettings specific
    monthly_rent: "",
    management_type: "fully_managed",
    management_fee_percent: 10,
    tenant_find_fee: "",
    furnished_type: "unfurnished",
    available_date: "",
    
    // Property info
    epc_rating: "",
    council_tax_band: "",
    tenure: "freehold",
    lease_years_remaining: "",
    ground_rent: "",
    service_charge: "",
    
    // Compliance (lettings)
    epc_expiry: "",
    gas_safety_expiry: "",
    eicr_expiry: "",
    
    // Key holder
    key_holder_name: "",
    key_holder_phone: "",
    access_notes: "",
    
    // Notes
    notes: ""
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.address_line1 || !formData.town || !formData.postcode) {
      toast.error("Please fill in the property address");
      return;
    }
    
    if (instructionType === "sales" && !formData.asking_price) {
      toast.error("Please enter the asking price");
      return;
    }
    
    if (instructionType === "lettings" && !formData.monthly_rent) {
      toast.error("Please enter the monthly rent");
      return;
    }
    
    setLoading(true);
    
    try {
      const payload = {
        applicant_id: applicantId || "standalone",
        address: {
          line_1: formData.address_line1,
          line_2: formData.address_line2 || null,
          town: formData.town,
          county: formData.county || null,
          postcode: formData.postcode.toUpperCase()
        },
        property_type: formData.property_type,
        bedrooms: parseInt(formData.bedrooms) || 1,
        bathrooms: parseInt(formData.bathrooms) || 1,
        receptions: parseInt(formData.receptions) || 1,
        key_holder_name: formData.key_holder_name || null,
        key_holder_phone: formData.key_holder_phone || null,
        access_notes: formData.access_notes || null,
        notes: formData.notes || null
      };
      
      if (instructionType === "sales") {
        payload.asking_price = parseFloat(formData.asking_price) || 0;
        payload.agency_type = formData.agency_type || "sole";
        payload.fee_percentage = parseFloat(formData.fee_percentage) || 1.25;
        payload.fee_fixed = formData.fee_fixed ? parseFloat(formData.fee_fixed) : null;
        payload.instruction_date = formData.instruction_date || new Date().toISOString().split("T")[0];
        payload.marketing_start_date = formData.marketing_start_date || null;
        payload.epc_rating = formData.epc_rating || null;
        payload.council_tax_band = formData.council_tax_band || null;
        payload.tenure = formData.tenure || "freehold";
        payload.lease_years_remaining = formData.lease_years_remaining ? parseInt(formData.lease_years_remaining) : null;
        payload.ground_rent = formData.ground_rent ? parseFloat(formData.ground_rent) : null;
        payload.service_charge = formData.service_charge ? parseFloat(formData.service_charge) : null;
      } else {
        payload.monthly_rent = parseFloat(formData.monthly_rent);
        payload.management_type = formData.management_type;
        payload.management_fee_percent = parseFloat(formData.management_fee_percent);
        payload.tenant_find_fee = formData.tenant_find_fee ? parseFloat(formData.tenant_find_fee) : null;
        payload.furnished_type = formData.furnished_type;
        payload.available_date = formData.available_date;
        payload.epc_rating = formData.epc_rating || null;
        payload.epc_expiry = formData.epc_expiry || null;
        payload.gas_safety_expiry = formData.gas_safety_expiry || null;
        payload.eicr_expiry = formData.eicr_expiry || null;
      }
      
      const endpoint = instructionType === "sales" ? "/instructions/sales" : "/instructions/lettings";
      const response = await axios.post(`${API}${endpoint}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Property instruction created successfully");
      onSuccess?.(response.data);
      onOpenChange(false);
      
      // Reset form
      setFormData({
        address_line1: "", address_line2: "", town: "", county: "", postcode: "",
        property_type: "semi_detached", bedrooms: 3, bathrooms: 1, receptions: 1,
        asking_price: "", agency_type: "sole", fee_percentage: 1.25, fee_fixed: "",
        instruction_date: new Date().toISOString().split("T")[0], marketing_start_date: "",
        monthly_rent: "", management_type: "fully_managed", management_fee_percent: 10,
        tenant_find_fee: "", furnished_type: "unfurnished", available_date: "",
        epc_rating: "", council_tax_band: "", tenure: "freehold", lease_years_remaining: "",
        ground_rent: "", service_charge: "", epc_expiry: "", gas_safety_expiry: "",
        eicr_expiry: "", key_holder_name: "", key_holder_phone: "", access_notes: "", notes: ""
      });
    } catch (error) {
      console.error("Error creating instruction:", error);
      const errorDetail = error.response?.data?.detail;
      // Handle validation errors which come as an array of objects
      if (Array.isArray(errorDetail)) {
        const errorMsg = errorDetail.map(e => e.msg || e.message || JSON.stringify(e)).join(", ");
        toast.error(errorMsg || "Validation error");
      } else if (typeof errorDetail === 'object') {
        toast.error(errorDetail.msg || errorDetail.message || "Failed to create instruction");
      } else {
        toast.error(errorDetail || "Failed to create instruction");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="w-5 h-5" />
            Add New Property Instruction
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            Creating instruction for: <span className="font-medium">{applicantName}</span>
          </p>
        </DialogHeader>

        <div className="space-y-6">
          {/* Instruction Type Toggle */}
          <div className="flex gap-2">
            <Button
              type="button"
              variant={instructionType === "sales" ? "default" : "outline"}
              onClick={() => setInstructionType("sales")}
              className="flex-1"
              data-testid="instruction-type-sales"
            >
              <Home className="w-4 h-4 mr-2" />
              Sales
            </Button>
            <Button
              type="button"
              variant={instructionType === "lettings" ? "default" : "outline"}
              onClick={() => setInstructionType("lettings")}
              className="flex-1"
              data-testid="instruction-type-lettings"
            >
              <Building2 className="w-4 h-4 mr-2" />
              Lettings
            </Button>
          </div>

          <Tabs defaultValue="address" className="w-full">
            <TabsList className="grid grid-cols-4 w-full">
              <TabsTrigger value="address">Address</TabsTrigger>
              <TabsTrigger value="property">Property</TabsTrigger>
              <TabsTrigger value="terms">
                {instructionType === "sales" ? "Sales Terms" : "Letting Terms"}
              </TabsTrigger>
              <TabsTrigger value="access">Access & Notes</TabsTrigger>
            </TabsList>

            {/* Address Tab */}
            <TabsContent value="address" className="space-y-4 mt-4">
              {/* Postcode Lookup */}
              <PostcodeLookup
                onAddressSelect={(addr) => {
                  handleChange("address_line1", addr.line_1);
                  handleChange("address_line2", addr.line_2 || "");
                  handleChange("town", addr.town);
                  handleChange("county", addr.county || "");
                  handleChange("postcode", addr.postcode);
                }}
              />

              <div className="border-t pt-4">
                <p className="text-xs text-slate-500 mb-3">Or enter address manually:</p>
              </div>

              <div className="grid grid-cols-1 gap-4">
                <div>
                  <Label htmlFor="address_line1">Address Line 1 *</Label>
                  <Input
                    id="address_line1"
                    value={formData.address_line1}
                    onChange={(e) => handleChange("address_line1", e.target.value)}
                    placeholder="e.g. 123 High Street"
                    data-testid="address-line1"
                  />
                </div>
                <div>
                  <Label htmlFor="address_line2">Address Line 2</Label>
                  <Input
                    id="address_line2"
                    value={formData.address_line2}
                    onChange={(e) => handleChange("address_line2", e.target.value)}
                    placeholder="e.g. Flat 2"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="town">Town/City *</Label>
                    <Input
                      id="town"
                      value={formData.town}
                      onChange={(e) => handleChange("town", e.target.value)}
                      placeholder="e.g. Bedford"
                      data-testid="town"
                    />
                  </div>
                  <div>
                    <Label htmlFor="county">County</Label>
                    <Input
                      id="county"
                      value={formData.county}
                      onChange={(e) => handleChange("county", e.target.value)}
                      placeholder="e.g. Bedfordshire"
                    />
                  </div>
                </div>
                <div className="w-1/3">
                  <Label htmlFor="postcode">Postcode *</Label>
                  <Input
                    id="postcode"
                    value={formData.postcode}
                    onChange={(e) => handleChange("postcode", e.target.value.toUpperCase())}
                    placeholder="e.g. MK42 7AB"
                    data-testid="postcode"
                  />
                </div>
              </div>
            </TabsContent>

            {/* Property Tab */}
            <TabsContent value="property" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="property_type">Property Type</Label>
                  <Select value={formData.property_type} onValueChange={(v) => handleChange("property_type", v)}>
                    <SelectTrigger data-testid="property-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="detached">Detached House</SelectItem>
                      <SelectItem value="semi_detached">Semi-Detached House</SelectItem>
                      <SelectItem value="terraced">Terraced House</SelectItem>
                      <SelectItem value="end_terrace">End Terrace</SelectItem>
                      <SelectItem value="flat">Flat/Apartment</SelectItem>
                      <SelectItem value="maisonette">Maisonette</SelectItem>
                      <SelectItem value="bungalow">Bungalow</SelectItem>
                      <SelectItem value="cottage">Cottage</SelectItem>
                      <SelectItem value="studio">Studio</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="tenure">Tenure</Label>
                  <Select value={formData.tenure} onValueChange={(v) => handleChange("tenure", v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="freehold">Freehold</SelectItem>
                      <SelectItem value="leasehold">Leasehold</SelectItem>
                      <SelectItem value="share_of_freehold">Share of Freehold</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="bedrooms">Bedrooms</Label>
                  <Select value={formData.bedrooms.toString()} onValueChange={(v) => handleChange("bedrooms", v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[0, 1, 2, 3, 4, 5, 6, 7, 8].map(n => (
                        <SelectItem key={n} value={n.toString()}>{n}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="bathrooms">Bathrooms</Label>
                  <Select value={formData.bathrooms.toString()} onValueChange={(v) => handleChange("bathrooms", v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[1, 2, 3, 4, 5].map(n => (
                        <SelectItem key={n} value={n.toString()}>{n}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="receptions">Reception Rooms</Label>
                  <Select value={formData.receptions.toString()} onValueChange={(v) => handleChange("receptions", v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[0, 1, 2, 3, 4].map(n => (
                        <SelectItem key={n} value={n.toString()}>{n}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="epc_rating">EPC Rating</Label>
                  <Select value={formData.epc_rating} onValueChange={(v) => handleChange("epc_rating", v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      {["A", "B", "C", "D", "E", "F", "G"].map(r => (
                        <SelectItem key={r} value={r}>{r}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="council_tax_band">Council Tax Band</Label>
                  <Select value={formData.council_tax_band} onValueChange={(v) => handleChange("council_tax_band", v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      {["A", "B", "C", "D", "E", "F", "G", "H"].map(b => (
                        <SelectItem key={b} value={b}>{b}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {formData.tenure === "leasehold" && (
                <div className="grid grid-cols-3 gap-4 p-4 bg-amber-50 rounded-lg">
                  <div>
                    <Label htmlFor="lease_years_remaining">Lease Years Remaining</Label>
                    <Input
                      id="lease_years_remaining"
                      type="number"
                      value={formData.lease_years_remaining}
                      onChange={(e) => handleChange("lease_years_remaining", e.target.value)}
                      placeholder="e.g. 95"
                    />
                  </div>
                  <div>
                    <Label htmlFor="ground_rent">Ground Rent (£/year)</Label>
                    <Input
                      id="ground_rent"
                      type="number"
                      value={formData.ground_rent}
                      onChange={(e) => handleChange("ground_rent", e.target.value)}
                      placeholder="e.g. 250"
                    />
                  </div>
                  <div>
                    <Label htmlFor="service_charge">Service Charge (£/year)</Label>
                    <Input
                      id="service_charge"
                      type="number"
                      value={formData.service_charge}
                      onChange={(e) => handleChange("service_charge", e.target.value)}
                      placeholder="e.g. 1200"
                    />
                  </div>
                </div>
              )}
            </TabsContent>

            {/* Sales/Letting Terms Tab */}
            <TabsContent value="terms" className="space-y-4 mt-4">
              {instructionType === "sales" ? (
                <>
                  {/* Sales Terms */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="asking_price">Asking Price (£) *</Label>
                      <Input
                        id="asking_price"
                        type="number"
                        value={formData.asking_price}
                        onChange={(e) => handleChange("asking_price", e.target.value)}
                        placeholder="e.g. 350000"
                        data-testid="asking-price"
                      />
                    </div>
                    <div>
                      <Label htmlFor="agency_type">Agency Type</Label>
                      <Select value={formData.agency_type} onValueChange={(v) => handleChange("agency_type", v)}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="sole">Sole Agency</SelectItem>
                          <SelectItem value="multi">Multi Agency</SelectItem>
                          <SelectItem value="joint_sole">Joint Sole Agency</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="fee_percentage">Fee Percentage (%)</Label>
                      <Input
                        id="fee_percentage"
                        type="number"
                        step="0.01"
                        value={formData.fee_percentage}
                        onChange={(e) => handleChange("fee_percentage", e.target.value)}
                        placeholder="e.g. 1.25"
                      />
                    </div>
                    <div>
                      <Label htmlFor="fee_fixed">Or Fixed Fee (£)</Label>
                      <Input
                        id="fee_fixed"
                        type="number"
                        value={formData.fee_fixed}
                        onChange={(e) => handleChange("fee_fixed", e.target.value)}
                        placeholder="e.g. 4995"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="instruction_date">Instruction Date *</Label>
                      <Input
                        id="instruction_date"
                        type="date"
                        value={formData.instruction_date}
                        onChange={(e) => handleChange("instruction_date", e.target.value)}
                        data-testid="instruction-date"
                      />
                    </div>
                    <div>
                      <Label htmlFor="marketing_start_date">Marketing Start Date</Label>
                      <Input
                        id="marketing_start_date"
                        type="date"
                        value={formData.marketing_start_date}
                        onChange={(e) => handleChange("marketing_start_date", e.target.value)}
                      />
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {/* Lettings Terms */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="monthly_rent">Monthly Rent (£) *</Label>
                      <Input
                        id="monthly_rent"
                        type="number"
                        value={formData.monthly_rent}
                        onChange={(e) => handleChange("monthly_rent", e.target.value)}
                        placeholder="e.g. 950"
                        data-testid="monthly-rent"
                      />
                    </div>
                    <div>
                      <Label htmlFor="management_type">Management Type</Label>
                      <Select value={formData.management_type} onValueChange={(v) => handleChange("management_type", v)}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="let_only">Tenant Find</SelectItem>
                          <SelectItem value="rent_collect">Rent Collection</SelectItem>
                          <SelectItem value="hybrid">Hybrid</SelectItem>
                          <SelectItem value="fully_managed">Fully Managed</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="management_fee_percent">Management Fee (%)</Label>
                      <Input
                        id="management_fee_percent"
                        type="number"
                        step="0.5"
                        value={formData.management_fee_percent}
                        onChange={(e) => handleChange("management_fee_percent", e.target.value)}
                        placeholder="e.g. 10"
                      />
                    </div>
                    <div>
                      <Label htmlFor="tenant_find_fee">Tenant Find Fee (£)</Label>
                      <Input
                        id="tenant_find_fee"
                        type="number"
                        value={formData.tenant_find_fee}
                        onChange={(e) => handleChange("tenant_find_fee", e.target.value)}
                        placeholder="e.g. 500"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="furnished_type">Furnished Status</Label>
                      <Select value={formData.furnished_type} onValueChange={(v) => handleChange("furnished_type", v)}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="furnished">Furnished</SelectItem>
                          <SelectItem value="part_furnished">Part Furnished</SelectItem>
                          <SelectItem value="unfurnished">Unfurnished</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="available_date">Available From</Label>
                      <Input
                        id="available_date"
                        type="date"
                        value={formData.available_date}
                        onChange={(e) => handleChange("available_date", e.target.value)}
                        data-testid="available-date"
                      />
                    </div>
                  </div>

                  {/* Compliance Dates */}
                  <div className="p-4 bg-slate-50 rounded-lg space-y-4">
                    <h4 className="font-medium text-sm">Compliance Certificates</h4>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label htmlFor="epc_expiry">EPC Expiry</Label>
                        <Input
                          id="epc_expiry"
                          type="date"
                          value={formData.epc_expiry}
                          onChange={(e) => handleChange("epc_expiry", e.target.value)}
                        />
                      </div>
                      <div>
                        <Label htmlFor="gas_safety_expiry">Gas Safety Expiry</Label>
                        <Input
                          id="gas_safety_expiry"
                          type="date"
                          value={formData.gas_safety_expiry}
                          onChange={(e) => handleChange("gas_safety_expiry", e.target.value)}
                        />
                      </div>
                      <div>
                        <Label htmlFor="eicr_expiry">EICR Expiry</Label>
                        <Input
                          id="eicr_expiry"
                          type="date"
                          value={formData.eicr_expiry}
                          onChange={(e) => handleChange("eicr_expiry", e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}
            </TabsContent>

            {/* Access & Notes Tab */}
            <TabsContent value="access" className="space-y-4 mt-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Key className="w-4 h-4" />
                    Key Holder / Access
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="key_holder_name">Key Holder Name</Label>
                      <Input
                        id="key_holder_name"
                        value={formData.key_holder_name}
                        onChange={(e) => handleChange("key_holder_name", e.target.value)}
                        placeholder="e.g. Owner / Tenant / Office"
                      />
                    </div>
                    <div>
                      <Label htmlFor="key_holder_phone">Key Holder Phone</Label>
                      <Input
                        id="key_holder_phone"
                        value={formData.key_holder_phone}
                        onChange={(e) => handleChange("key_holder_phone", e.target.value)}
                        placeholder="e.g. 07700 900123"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="access_notes">Access Notes</Label>
                    <Textarea
                      id="access_notes"
                      value={formData.access_notes}
                      onChange={(e) => handleChange("access_notes", e.target.value)}
                      placeholder="e.g. Call 24hrs before viewing. Parking available on drive."
                      rows={2}
                    />
                  </div>
                </CardContent>
              </Card>

              <div>
                <Label htmlFor="notes">Additional Notes</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => handleChange("notes", e.target.value)}
                  placeholder="Any other notes about this instruction..."
                  rows={3}
                />
              </div>
            </TabsContent>
          </Tabs>

          {/* Submit Button */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={loading} data-testid="create-instruction-btn">
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Instruction
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Instruction Card Component - for displaying instruction status
export const InstructionCard = ({ instruction, onUpdate }) => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const [loading, setLoading] = useState(false);

  const statusColors = {
    draft: "bg-slate-100 text-slate-700",
    pending_approval: "bg-amber-100 text-amber-700",
    approved: "bg-blue-100 text-blue-700",
    live: "bg-emerald-100 text-emerald-700",
    sold: "bg-purple-100 text-purple-700",
    let: "bg-purple-100 text-purple-700",
    withdrawn: "bg-red-100 text-red-700"
  };

  const handleCardClick = () => {
    // Navigate to Property Hub for instructions
    navigate(`/sales/property-hub/${instruction.instruction_id}`);
  };

  const handleAction = async (action) => {
    setLoading(true);
    try {
      await axios.post(
        `${API}/instructions/${instruction.instruction_id}/${action}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Action completed: ${action.replace("-", " ")}`);
      onUpdate?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Action failed");
    } finally {
      setLoading(false);
    }
  };

  const WorkflowStep = ({ completed, label, date }) => (
    <div className="flex items-center gap-2">
      {completed ? (
        <CheckCircle2 className="w-4 h-4 text-emerald-500" />
      ) : (
        <Circle className="w-4 h-4 text-slate-300" />
      )}
      <span className={completed ? "text-slate-700" : "text-slate-400"}>
        {label}
      </span>
      {date && (
        <span className="text-xs text-muted-foreground">
          {new Date(date).toLocaleDateString()}
        </span>
      )}
    </div>
  );

  return (
    <Card 
      className="hover:shadow-md transition-shadow cursor-pointer"
      onClick={handleCardClick}
      data-testid="instruction-card"
    >
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <div>
              <CardTitle className="text-lg">{instruction.full_address}</CardTitle>
              <p className="text-sm text-muted-foreground">
                Ref: {instruction.property_ref} • {instruction.type === "sales" ? "Sales" : "Lettings"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge className={statusColors[instruction.status] || "bg-slate-100"}>
              {instruction.status?.replace("_", " ")}
            </Badge>
            <ExternalLink className="w-4 h-4 text-muted-foreground" />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <p className="text-sm text-muted-foreground">
              {instruction.type === "sales" ? "Asking Price" : "Monthly Rent"}
            </p>
            <p className="font-semibold text-lg">
              £{(instruction.asking_price || instruction.monthly_rent)?.toLocaleString()}
              {instruction.type === "lettings" && " pcm"}
            </p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Property</p>
            <p className="font-medium">
              {instruction.bedrooms} bed {instruction.property_type?.replace("_", " ")}
            </p>
          </div>
        </div>

        {/* Workflow Progress */}
        <div className="space-y-2 py-3 border-t border-b">
          <p className="text-xs font-medium text-muted-foreground uppercase">Workflow Progress</p>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <WorkflowStep
              completed={instruction.draft_approved}
              label="Draft Approved"
              date={instruction.draft_approved_at}
            />
            <WorkflowStep
              completed={instruction.contract_signed}
              label="Contract Signed"
              date={instruction.contract_signed_at}
            />
            <WorkflowStep
              completed={instruction.ids_uploaded}
              label="IDs Uploaded"
            />
            <WorkflowStep
              completed={instruction.portal_live}
              label="Live on Portals"
              date={instruction.portal_live_at}
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2 mt-4" onClick={(e) => e.stopPropagation()}>
          {!instruction.draft_approved && instruction.status === "draft" && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleAction("send-draft")}
              disabled={loading}
            >
              <Send className="w-3 h-3 mr-1" />
              Send Draft
            </Button>
          )}
          {instruction.draft_approved && !instruction.contract_signed && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleAction("send-contract")}
              disabled={loading}
            >
              <FileText className="w-3 h-3 mr-1" />
              Send Contract
            </Button>
          )}
          {instruction.contract_signed && !instruction.portal_live && instruction.ids_uploaded && (
            <Button
              size="sm"
              onClick={() => handleAction("go-live")}
              disabled={loading}
            >
              <Upload className="w-3 h-3 mr-1" />
              Go Live
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default AddInstructionModal;
